import useCustomToast from "@/hooks/useCustomToast";
import { Box, Button, Input, Text, Textarea, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../ui/dialog";
const validateFolderName = (name: string): string | null => {
  if (!name.trim()) {
    return "폴더명을 입력하세요.";
  }
  // 한글, 영어, 숫자, 공백, 일부 특수문자(-, _, .) 허용
  if (!/^[a-zA-Z0-9가-힣\s\-_.]+$/.test(name)) {
    return "폴더명은 한글, 영어, 숫자, 공백, 특수문자(-, _, .)만 사용할 수 있습니다.";
  }
  if (name.length < 2 || name.length > 20) {
    return "폴더명은 2~20자 사이여야 합니다.";
  }
  return null;
};

const validateDescription = (description: string): string | null => {
  if (!description.trim()) {
    return "폴더 설명을 입력하세요.";
  }
  return null;
};

const AddFolder = ({
  isOpen,
  setIsOpen,
  onAddFolder,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onAddFolder: (folderName: string, description: string) => void;
}) => {
  const [folderName, setFolderName] = useState("");
  const [description, setDescription] = useState("");
  const [folderNameError, setFolderNameError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useCustomToast();

  useEffect(() => {
    if (!isOpen) {
      setFolderName("");
      setDescription("");
      setFolderNameError(null);
      setDescriptionError(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFolderName(value);
    // Validate folder name on change for immediate feedback
    setFolderNameError(validateFolderName(value));
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setDescription(value);
    // Validate description on change for immediate feedback
    setDescriptionError(validateDescription(value));
  };

  const handleAdd = async () => {
    // Re-validate on submission
    const currentFolderNameError = validateFolderName(folderName);
    const currentDescriptionError = validateDescription(description);

    setFolderNameError(currentFolderNameError);
    setDescriptionError(currentDescriptionError);

    if (currentFolderNameError || currentDescriptionError) {
      return;
    }

    try {
      setIsLoading(true);
      await onAddFolder(folderName.trim(), description.trim());
      setFolderName("");
      setDescription("");
      setFolderNameError(null);
      setDescriptionError(null);
      setIsOpen(false);
    } catch (error) {
      console.error("폴더 생성 중 오류:", error);
      toast.showErrorToast("폴더 생성에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>폴더 추가</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text mb={4}>폴더 정보를 입력하세요.</Text>
          <VStack gap={4} align="stretch">
            <Box>
              <Text mb={2} fontWeight="medium">
                폴더명{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
              <Input
                placeholder="폴더명을 입력하세요 (2~20자)"
                value={folderName}
                onChange={handleNameChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isLoading) handleAdd();
                }}
                borderColor={folderNameError ? "red.500" : undefined}
                disabled={isLoading}
                autoFocus
              />
              {folderNameError && (
                <Text
                  fontSize="xs"
                  color="red.500"
                  mt={1}
                  data-testid="folder-name-error"
                >
                  {folderNameError}
                </Text>
              )}
            </Box>
            <Box>
              <Text mb={2} fontWeight="medium">
                폴더 설명{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
              <Textarea
                placeholder="폴더 설명을 입력하세요"
                value={description}
                onChange={handleDescriptionChange}
                disabled={isLoading}
                borderColor={descriptionError ? "red.500" : undefined}
              />
              {descriptionError && (
                <Text
                  fontSize="xs"
                  color="red.500"
                  mt={1}
                  data-testid="description-error"
                >
                  {descriptionError}
                </Text>
              )}
            </Box>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              onClick={() => setIsOpen(false)}
              mr={2}
              disabled={isLoading}
            >
              취소
            </Button>
          </DialogActionTrigger>
          <Button
            colorScheme="blue"
            onClick={handleAdd}
            loading={isLoading}
            loadingText="생성 중..."
          >
            추가
          </Button>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default AddFolder;
